import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { DropZone } from "@/components/DropZone";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSuspendedClientGuard } from "@/context/SuspendedClientAlert";
import { useUnsavedGuard } from "@/context/UnsavedChangesGuard";
import { InternalLayout } from "@/components/InternalLayout";
import { RichEditor, isEmptyHtml } from "@/components/RichEditor";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { TimeField } from "@/components/TimeField";
import { toast } from "sonner";
import { CaretLeftIcon as CaretLeft, FilePdfIcon as FilePdf, PaperPlaneTiltIcon as PaperPlaneTilt, FloppyDiskIcon as FloppyDisk, PlusIcon as Plus, TrashIcon as Trash, CopyIcon as Copy, MagnifyingGlassIcon as MagnifyingGlass, UserPlusIcon as UserPlus, XCircleIcon as XCircle, BuildingsIcon as Buildings, NavigationArrowIcon as NavigationArrow, MapPinIcon as MapPin, ArrowsClockwiseIcon as ArrowsClockwise, PaperclipIcon as Paperclip, DownloadSimpleIcon as DownloadSimple, UploadSimpleIcon as UploadSimple, WarningIcon as Warning, LockSimpleIcon as LockSimple, TicketIcon as Ticket, LinkSimpleIcon as LinkSimple, LinkBreakIcon as LinkBreak, WrenchIcon as Wrench, ArrowSquareOutIcon as ArrowSquareOut, CalendarBlankIcon as CalendarBlank } from "@phosphor-icons/react";
const fmtSize = (b) => { const n = Number(b) || 0; if (n < 1024) return `${n} B`; if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`; return `${(n / 1048576).toFixed(1)} MB`; };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtAddr = (o) => [o?.indirizzo, [o?.cap, o?.citta].filter(Boolean).join(" ")].filter(Boolean).join(", ").trim();
function buildSedi(c) {
  if (!c) return [];
  const out = [];
  const main = fmtAddr(c);
  if (main) out.push({ id: "principale", nome: "Sede principale", indirizzo: main });
  const sedi = Array.isArray(c.sedi) ? c.sedi : [];
  sedi.forEach((s, i) => { const a = fmtAddr(s); if (a) out.push({ id: `sede-${i}`, nome: s.nome || `Sede ${i + 1}`, indirizzo: a }); });
  return out;
}
const EMPTY_CLI = { ragione_sociale: "", indirizzo: "", citta: "", cap: "", email: "", telefono: "" };

function buildForm(data) {
  return {
    cliente_id: data.cliente_id || "", cliente_nome: data.cliente_nome || "", luogo: data.luogo || "",
    stato: data.stato || "pianificata", modalita_addebito: data.modalita_addebito || "consuntivo",
    data_richiesta: (data.data_richiesta || "").slice(0, 10), data_scadenza: (data.data_scadenza || "").slice(0, 10),
    zona: data.zona || "", veicolo: data.veicolo || "", contratto_id: data.contratto_id || "none",
    sottocontratto_codice: data.sottocontratto_codice || "",
    support_contract_id: data.support_contract_id || "none", richiesta_intervento: data.richiesta_intervento || "",
    descrizione_intervento: data.descrizione_intervento || "", note_interne: data.note_interne || "",
    sede_id: data.sede_id || "", maps_lat: data.maps_lat ?? null, maps_lng: data.maps_lng ?? null,
    maps_place_id: data.maps_place_id || "", maps_last_geocoded_at: data.maps_last_geocoded_at || "",
  };
}

// Chiavi monitorate per il dirty tracking (esclusi i campi maps_* derivati dal geocoding automatico)
const SNAP_KEYS = ["cliente_id", "cliente_nome", "luogo", "sede_id", "stato", "modalita_addebito", "contratto_id", "sottocontratto_codice", "support_contract_id", "veicolo", "zona", "data_richiesta", "richiesta_intervento", "descrizione_intervento", "note_interne"];
const MESI_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const HTML_KEYS = new Set(["richiesta_intervento", "descrizione_intervento", "note_interne"]);
// Collassa i placeholder "vuoti" di Quill ('<p><br></p>', '<p></p>', ...) a "" per evitare dirty spurio
function normHtml(v) {
  if (typeof v !== "string") return "";
  const t = v.replace(/\s/g, "").toLowerCase();
  if (t === "" || t === "<p></p>" || t === "<p><br></p>" || t === "<p><br/></p>") return "";
  return v;
}
function makeSnap(f, sess) {
  const ff = {}; SNAP_KEYS.forEach((k) => { ff[k] = HTML_KEYS.has(k) ? normHtml(f?.[k]) : (f?.[k] ?? ""); });
  const ss = (sess || []).map((s) => ({
    tecnico_id: s.tecnico_id || "", data: (s.data || "").slice(0, 10), ora_inizio: s.ora_inizio || "", ora_fine: s.ora_fine || "",
    pausa_inizio: s.pausa_inizio || "", pausa_fine: s.pausa_fine || "", km: s.km ?? 0, sconto_ore: s.sconto_ore ?? 0,
    sconto_km: s.sconto_km ?? 0, modalita_addebito: s.modalita_addebito || "", note: s.note || "",
  }));
  return JSON.stringify({ ff, ss });
}

const TEC_COLORS = ["#2563eb", "#16a34a", "#db2777", "#ea580c", "#7c3aed", "#0891b2", "#ca8a04", "#dc2626"];
const hm = (min) => `${Math.floor((min || 0) / 60)}h ${String(Math.round((min || 0) % 60)).padStart(2, "0")}m`;
const toMin = (v) => { if (!v) return null; const [h, m] = String(v).split(":").map(Number); return (h * 60 + (m || 0)); };
const pausaMin = (s) => { const a = toMin(s.pausa_inizio), b = toMin(s.pausa_fine); return (a != null && b != null) ? Math.max(0, b - a) : 0; };
const sessMin = (s) => { const a = toMin(s.ora_inizio), b = toMin(s.ora_fine); if (a == null || b == null) return 0; return Math.max(0, (b - a) - pausaMin(s)); };

// Validazione singola sessione -> stringa errore o null
function sessError(s) {
  const a = toMin(s.ora_inizio), b = toMin(s.ora_fine);
  if (a != null && b != null && b <= a) return "L'orario di fine deve essere successivo all'inizio";
  const pi = toMin(s.pausa_inizio), pf = toMin(s.pausa_fine);
  if ((pi != null) !== (pf != null)) return "Compila entrambi gli orari di pausa (dalle e alle)";
  if (pi != null && pf != null) {
    if (pf <= pi) return "La pausa deve terminare dopo l'inizio pausa";
    if (a != null && b != null && (pi < a || pf > b)) return "La pausa deve essere all'interno dell'orario di lavoro";
  }
  return null;
}

const EMPTY_SESS = () => ({ tecnico_id: "", data: new Date().toISOString().slice(0, 10), ora_inizio: "09:00", ora_fine: "10:00", pausa_inizio: "", pausa_fine: "", km: 0, sconto_ore: 0, sconto_km: 0, modalita_addebito: "", note: "" });

export default function AttivitaDetail() {
  const { id } = useParams();
  const isNew = id === "nuovo";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [cfg, setCfg] = useState(null);
  const [numero, setNumero] = useState("");
  const [contrattoAlert, setContrattoAlert] = useState(null);
  const [tecnici, setTecnici] = useState([]);
  const [veicoli, setVeicoli] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [scContracts, setScContracts] = useState([]);
  const [form, setForm] = useState(null);
  useSuspendedClientGuard(form?.cliente_id);
  const modalitaTouchedRef = useRef(false);
  const zonaTouchedRef = useRef(false);
  const contrattoTouchedRef = useRef(false);
  const CAT_LABELS = { assistenza: "Assistenza", flat: "FLAT", manutenzione: "Manutenzione" };
  const isScaduto = (c) => !!(c?.data_scadenza && c.data_scadenza.slice(0, 10) < new Date().toISOString().slice(0, 10) && c.stato === "attivo");
  const linkableContracts = useMemo(() => (contracts || []).filter((c) => ["assistenza", "flat", "manutenzione"].includes(c.categoria) && (c.stato !== "cessato" || c.id === form?.contratto_id)), [contracts, form?.contratto_id]);
  const selectedContract = useMemo(() => (contracts || []).find((c) => c.id === form?.contratto_id), [contracts, form?.contratto_id]);
  const sottoOptions = useMemo(() => {
    if (!(selectedContract && selectedContract.categoria === "flat" && (selectedContract.sottocontratti || []).length > 0)) return [];
    const nome = selectedContract.nome || "";
    return (selectedContract.sottocontratti || [])
      .filter((s) => Number(s.anno) && Number(s.mese) && !s.concluso_fatturato)
      .map((s) => ({ ...s, codice: s.codice || `${nome}/${Number(s.anno)}-${String(Number(s.mese)).padStart(2, "0")}` }));
  }, [selectedContract]);
  const [sessioni, setSessioni] = useState([]);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  // Dialog invio rapportino
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);

  // Client autocomplete
  const [cliQuery, setCliQuery] = useState("");
  const [cliResults, setCliResults] = useState([]);
  const [cliOpen, setCliOpen] = useState(false);
  const cliBox = useRef(null);

  // Sedi cliente
  const [clientSedi, setClientSedi] = useState([]);

  // Quick-create anagrafica
  const [newCliOpen, setNewCliOpen] = useState(false);
  const [newCli, setNewCli] = useState(EMPTY_CLI);
  const [newCliSaving, setNewCliSaving] = useState(false);

  // Maps
  const [mapsConfigured, setMapsConfigured] = useState(null); // null=sconosciuto
  const [mapsStatus, setMapsStatus] = useState("idle"); // idle|loading|found|notfound|error
  const [mapUrl, setMapUrl] = useState("");

  // Allegati + overlap
  const [allegati, setAllegati] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [overlapDlg, setOverlapDlg] = useState(null);

  // Dirty tracking
  const [baseline, setBaseline] = useState(null);
  const [leaveTarget, setLeaveTarget] = useState(null);
  // Stato SALVATO/originale dell'intervento (distinto dal valore selezionato nel form).
  // Il freeze si basa su questo, non sul valore temporaneo del form.
  const [savedStato, setSavedStato] = useState(null);

  // Clone session
  const [cloneIdx, setCloneIdx] = useState(null);
  const [cloneTecs, setCloneTecs] = useState([]);
  const [multiAddOpen, setMultiAddOpen] = useState(false);
  const [multiAddTecs, setMultiAddTecs] = useState([]);

  // Ticket collegati
  const [linkedTickets, setLinkedTickets] = useState([]);
  const [lavRef, setLavRef] = useState(null);
  const [tkLinkDlg, setTkLinkDlg] = useState(false);
  const [tkLinkQ, setTkLinkQ] = useState("");
  const [tkLinkResults, setTkLinkResults] = useState([]);

  // Lavorazioni collegate (molti-a-molti)
  const [linkedLavs, setLinkedLavs] = useState([]);
  const [lavLinkDlg, setLavLinkDlg] = useState(false);
  const [lavLinkQ, setLavLinkQ] = useState("");
  const [lavLinkResults, setLavLinkResults] = useState([]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Freeze basato sullo stato SALVATO (non sul valore temporaneo del form):
  // i campi restano bloccati finché il cambio stato non viene salvato.
  const frozen = !isNew && savedStato === "completata";
  // L'utente ha cambiato lo stato da "completata" ma non ha ancora salvato.
  const statoUnsavedFromCompleted = frozen && form?.stato !== "completata";
  const slotStep = cfg?.calendario?.slot_minuti || 30;
  const currentSnap = form ? makeSnap(form, sessioni) : null;
  const dirty = baseline != null && currentSnap != null && currentSnap !== baseline;
  const tecColor = useCallback((tid) => { const i = tecnici.findIndex((t) => t.id === tid); return i >= 0 ? TEC_COLORS[i % TEC_COLORS.length] : "#94a3b8"; }, [tecnici]);
  const tecName = useCallback((tid) => (tecnici.find((t) => t.id === tid) || {}).name || "Non assegnato", [tecnici]);

  // ---- Config-derived options ----
  const statiOpts = useMemo(() => {
    const list = (cfg?.stati || []).filter((s) => s.attivo !== false);
    if (form?.stato && !list.some((s) => s.key === form.stato)) list.push({ key: form.stato, label: form.stato, color: "#6b7280" });
    return list;
  }, [cfg, form?.stato]);
  const modalitaOpts = useMemo(() => (cfg?.modalita || []).filter((m) => m.attivo !== false), [cfg]);
  const zoneOpts = useMemo(() => {
    const list = (cfg?.zone || []).filter((z) => z.attivo !== false).map((z) => z.label);
    if (form?.zona && !list.includes(form.zona)) list.unshift(form.zona);
    return list;
  }, [cfg, form?.zona]);
  const modalitaLabel = useCallback((key) => (modalitaOpts.find((m) => m.key === key) || {}).label || key || "—", [modalitaOpts]);
  const statoMeta = useCallback((key) => statiOpts.find((s) => s.key === key) || { label: key, color: "#6b7280" }, [statiOpts]);

  // ---- Load ----
  const loadClientData = useCallback((cid, autoSelect = false) => {
    api.get("/contracts", { params: { cliente_id: cid } }).then((r) => {
      setContracts(r.data);
    }).catch(() => {});
    api.get("/support-contracts", { params: { cliente_id: cid } }).then((r) => setScContracts(r.data.filter((s) => s.tipo === "monte_ore"))).catch(() => {});
    api.get(`/clients/${cid}`).then(({ data }) => setClientSedi(buildSedi(data))).catch(() => setClientSedi([]));
  }, []);

  useEffect(() => {
    api.get("/tecnici").then(({ data }) => setTecnici([...data].sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {});
    api.get("/veicoli/scadenze").then(({ data }) => setVeicoli((data.veicoli || []).map((v) => ({ targa: v.targa, in_manutenzione: !!v.in_manutenzione })))).catch(() => {});
    api.get("/interventi-config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  // init form once config is available (for defaults) or existing data loaded
  useEffect(() => {
    if (!cfg) return;
    if (isNew) {
      if (form) return;
      const def = (cfg.stati || []).find((s) => s.default && s.attivo !== false) || (cfg.stati || [])[0];
      const defMod = (cfg.modalita || []).find((m) => m.default && m.attivo !== false) || (cfg.modalita || []).find((m) => m.attivo !== false) || (cfg.modalita || [])[0];
      const initForm = {
        cliente_id: "", cliente_nome: "", luogo: "", stato: def?.key || "pianificata",
        modalita_addebito: defMod?.key || "consuntivo", data_richiesta: new Date().toISOString().slice(0, 10),
        data_scadenza: "", zona: "", veicolo: "", contratto_id: "", support_contract_id: "none",
        richiesta_intervento: "", descrizione_intervento: "", note_interne: "",
        sede_id: "", maps_lat: null, maps_lng: null, maps_place_id: "", maps_last_geocoded_at: "",
      };
      setForm(initForm);
      // prefill sessione da slot calendario (query params)
      const qd = searchParams.get("data"), qi = searchParams.get("inizio"), qf = searchParams.get("fine");
      let initSess = [];
      if (qd && qi && qf) {
        initSess = [{ ...EMPTY_SESS(), tecnico_id: "", data: qd, ora_inizio: qi, ora_fine: qf, modalita_addebito: "" }];
      }
      setSessioni(initSess);
      setBaseline(makeSnap(initForm, initSess));
      const fromTicket = searchParams.get("from_ticket");
      if (fromTicket) {
        api.get(`/tickets/${fromTicket}/activities/create-draft`).then(({ data }) => {
          const applyDraft = (cli) => {
            const sedi = cli ? buildSedi(cli) : [];
            const primary = sedi[0];
            if (sedi.length) setClientSedi(sedi);
            const defMod = cli && cli.metodo_addebito_default_interventi
              && (cfg.modalita || []).some((m) => m.key === cli.metodo_addebito_default_interventi)
              ? cli.metodo_addebito_default_interventi : null;
            const merged = {
              ...initForm,
              cliente_id: data.cliente_id || "",
              cliente_nome: data.cliente_nome || (cli ? cli.ragione_sociale : ""),
              luogo: primary ? primary.indirizzo : "",
              sede_id: primary ? primary.id : "",
              zona: cli && cli.zona ? cli.zona : "",
              ...(defMod ? { modalita_addebito: defMod } : {}),
              richiesta_intervento: data.richiesta_intervento || "",
              descrizione_intervento: data.descrizione_intervento || "",
              note_interne: data.note_interne || "",
            };
            setForm(merged);
            setBaseline(makeSnap(merged, initSess));
          };
          if (data.cliente_id) {
            api.get(`/clients/${data.cliente_id}`)
              .then(({ data: cli }) => { applyDraft(cli); loadClientData(data.cliente_id, true); })
              .catch(() => { applyDraft(null); loadClientData(data.cliente_id, true); });
          } else {
            applyDraft(null);
          }
        }).catch(() => {});
      }
      const fromLavorazione = searchParams.get("from_lavorazione");
      if (fromLavorazione) {
        api.get(`/lavorazioni/${fromLavorazione}/attivita/create-draft`).then(({ data }) => {
          setLavRef({ id: data.lavorazione_id, numero: data.lavorazione_numero, titolo: data.lavorazione_titolo });
          const applyDraft = (cli) => {
            const sedi = cli ? buildSedi(cli) : [];
            const primary = sedi[0];
            if (sedi.length) setClientSedi(sedi);
            const defMod = cli && cli.metodo_addebito_default_interventi
              && (cfg.modalita || []).some((m) => m.key === cli.metodo_addebito_default_interventi)
              ? cli.metodo_addebito_default_interventi : null;
            const merged = {
              ...initForm,
              cliente_id: data.cliente_id || "",
              cliente_nome: data.cliente_nome || (cli ? cli.ragione_sociale : ""),
              luogo: primary ? primary.indirizzo : "",
              sede_id: primary ? primary.id : "",
              zona: cli && cli.zona ? cli.zona : "",
              stato: data.stato_default || initForm.stato,
              ...(defMod ? { modalita_addebito: defMod } : {}),
              richiesta_intervento: data.richiesta_intervento || "",
              descrizione_intervento: data.descrizione_intervento || "",
              note_interne: data.note_interne || "",
            };
            setForm(merged);
            setBaseline(makeSnap(merged, initSess));
          };
          if (data.cliente_id) {
            api.get(`/clients/${data.cliente_id}`)
              .then(({ data: cli }) => { applyDraft(cli); loadClientData(data.cliente_id, true); })
              .catch(() => { applyDraft(null); loadClientData(data.cliente_id, true); });
          } else {
            applyDraft(null);
          }
        }).catch(() => toast.error("Lavorazione non trovata"));
      }
    } else {
      api.get(`/attivita/${id}`).then(({ data }) => {
        setNumero(data.numero || "");
        const f = buildForm(data);
        setForm(f);
        setSavedStato(f.stato);
        setLavRef(data.lavorazione_id ? { id: data.lavorazione_id, numero: data.lavorazione_numero, titolo: data.lavorazione_titolo } : null);
        setSessioni(data.sessioni_lavoro || []);
        setAllegati(data.allegati || []);
        setContrattoAlert(data.contratto_alert || null);
        setBaseline(makeSnap(f, data.sessioni_lavoro || []));
        if (data.cliente_id) loadClientData(data.cliente_id);
      }).catch(() => toast.error("Intervento non trovato"));
      api.get(`/attivita/${id}/tickets`).then(({ data }) => setLinkedTickets(data)).catch(() => {});
      api.get(`/attivita/${id}/lavorazioni`).then(({ data }) => setLinkedLavs(data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, id]);

  // Client search (debounced)
  useEffect(() => {
    if (!cliOpen || cliQuery.trim().length < 2) { setCliResults([]); return; }
    const t = setTimeout(() => {
      api.get("/clients", { params: { q: cliQuery.trim() } }).then(({ data }) => setCliResults(data.slice(0, 8))).catch(() => setCliResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [cliQuery, cliOpen]);

  useEffect(() => {
    const onClick = (e) => { if (cliBox.current && !cliBox.current.contains(e.target)) setCliOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Avviso nativo del browser su refresh/chiusura tab con modifiche non salvate
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const geocode = useCallback(async (address) => {
    const addr = (address || "").trim();
    if (!addr) { setMapsStatus("idle"); return; }
    setMapsStatus("loading");
    try {
      const { data } = await api.get("/maps/geocode", { params: { q: addr } });
      setMapsConfigured(data.configured);
      if (!data.configured) { setMapsStatus("idle"); return; }
      if (data.found) {
        setForm((f) => ({ ...f, maps_lat: data.lat, maps_lng: data.lng, maps_place_id: data.place_id || "", maps_last_geocoded_at: new Date().toISOString() }));
        setMapsStatus("found");
      } else {
        setForm((f) => ({ ...f, maps_lat: null, maps_lng: null, maps_place_id: "" }));
        setMapsStatus("notfound");
      }
    } catch { setMapsStatus("error"); }
  }, []);

  // Geocoding con debounce sul luogo (nessuna chiamata ad ogni carattere)
  useEffect(() => {
    const addr = (form?.luogo || "").trim();
    if (!addr) { setMapsStatus("idle"); return; }
    const t = setTimeout(() => geocode(addr), 800);
    return () => clearTimeout(t);
  }, [form?.luogo, geocode]);

  // Anteprima mini-mappa (proxy backend, chiave mai esposta)
  useEffect(() => {
    if (mapsConfigured && form?.maps_lat != null && form?.maps_lng != null) {
      let url = "";
      api.get("/maps/staticmap", { params: { lat: form.maps_lat, lng: form.maps_lng }, responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setMapUrl(url); })
        .catch(() => setMapUrl(""));
      return () => { if (url) URL.revokeObjectURL(url); };
    }
    setMapUrl("");
  }, [mapsConfigured, form?.maps_lat, form?.maps_lng]);

  const selectClient = (c) => {
    const sedi = buildSedi(c);
    setClientSedi(sedi);
    const primary = sedi[0];
    const defMod = isNew && !modalitaTouchedRef.current && c.metodo_addebito_default_interventi
      && modalitaOpts.some((m) => m.key === c.metodo_addebito_default_interventi)
      ? c.metodo_addebito_default_interventi : null;
    const defZona = isNew && !zonaTouchedRef.current && c.zona ? c.zona : null;
    setForm((f) => ({
      ...f, cliente_id: c.id, cliente_nome: c.ragione_sociale,
      luogo: primary ? primary.indirizzo : (f.luogo || ""),
      sede_id: primary ? primary.id : "",
      contratto_id: "", support_contract_id: "none",
      ...(defMod ? { modalita_addebito: defMod } : {}),
      ...(defZona ? { zona: defZona } : {}),
    }));
    contrattoTouchedRef.current = false;
    setCliOpen(false); setCliQuery("");
    loadClientData(c.id, true);
  };

  const pickSede = (sedeId) => {    const s = clientSedi.find((x) => x.id === sedeId);
    setForm((f) => ({ ...f, sede_id: sedeId, luogo: s ? s.indirizzo : f.luogo }));
  };

  const createAnagrafica = async () => {
    if (!newCli.ragione_sociale.trim()) { toast.error("La ragione sociale è obbligatoria"); return; }
    setNewCliSaving(true);
    try {
      const { data } = await api.post("/clients", { ...newCli, ragione_sociale: newCli.ragione_sociale.trim() });
      toast.success("Anagrafica creata e selezionata");
      setNewCliOpen(false); setNewCli(EMPTY_CLI);
      selectClient(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setNewCliSaving(false); }
  };

  // ---- Maps ----
  const openInMaps = () => {
    const q = (form.maps_lat != null && form.maps_lng != null)
      ? `${form.maps_lat},${form.maps_lng}`
      : (form.luogo || "").trim();
    if (!q) { toast.error("Nessun luogo da aprire in Maps"); return; }
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`, "_blank", "noopener,noreferrer");
  };


  // ---- Sessions ----
  const addSess = () => { const first = tecnici[0]?.id || ""; setSessioni((s) => [...s, { ...EMPTY_SESS(), tecnico_id: first, modalita_addebito: form?.modalita_addebito || "" }]); };
  const updSess = (i, k, v) => setSessioni((s) => s.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const removeSess = (i) => setSessioni((s) => s.filter((_, j) => j !== i));

  const openClone = (i) => { setCloneIdx(i); setCloneTecs([]); };
  const doClone = () => {
    if (cloneIdx == null) return;
    const src = sessioni[cloneIdx];
    // Tecnici che hanno già una sessione nello stesso slot (data+orari): da escludere per evitare doppioni
    const busy = new Set(
      sessioni.filter((x) => x.data === src.data && x.ora_inizio === src.ora_inizio && x.ora_fine === src.ora_fine)
        .map((x) => x.tecnico_id).filter(Boolean)
    );
    const targets = [...new Set(cloneTecs)].filter((tid) => tid && tid !== src.tecnico_id && !busy.has(tid));
    if (targets.length === 0) { toast.error("Nessun tecnico valido: già presenti in questo slot"); return; }
    const { session_id, ...rest } = src; // eslint-disable-line no-unused-vars
    const nuove = targets.map((tid) => ({ ...rest, tecnico_id: tid }));
    setSessioni((s) => [...s, ...nuove]);
    setCloneIdx(null); setCloneTecs([]);
    toast.success(`${nuove.length} sessione/i clonata/e`);
  };

  // Multi-tecnico in creazione: crea una sessione (orari default o da slot) per ogni tecnico selezionato
  const openMultiAdd = () => { setMultiAddTecs([]); setMultiAddOpen(true); };
  const doMultiAdd = () => {
    const targets = [...new Set(multiAddTecs)].filter(Boolean);
    if (targets.length === 0) return;
    const base = sessioni[0]
      ? { data: sessioni[0].data, ora_inizio: sessioni[0].ora_inizio, ora_fine: sessioni[0].ora_fine }
      : {};
    const busy = new Set(sessioni.filter((x) => x.data === base.data && x.ora_inizio === base.ora_inizio && x.ora_fine === base.ora_fine).map((x) => x.tecnico_id).filter(Boolean));
    const toAdd = targets.filter((tid) => !busy.has(tid));
    if (toAdd.length === 0) { toast.error("Tecnici già presenti in questo slot"); return; }
    const nuove = toAdd.map((tid) => ({ ...EMPTY_SESS(), ...base, tecnico_id: tid, modalita_addebito: form?.modalita_addebito || "" }));
    setSessioni((s) => [...s, ...nuove]);
    setMultiAddOpen(false); setMultiAddTecs([]);
    toast.success(`${nuove.length} sessione/i aggiunta/e`);
  };

  const totMin = sessioni.reduce((a, s) => a + sessMin(s), 0);
  const perTec = useMemo(() => { const p = {}; sessioni.forEach((s) => { const t = s.tecnico_id || ""; p[t] = (p[t] || 0) + sessMin(s); }); return p; }, [sessioni]);

  // Blocco salvataggio: per ogni stato diverso da "Da pianificare" servono sessione + tecnico assegnato
  const sessionBlock = useMemo(() => {
    if (!form || frozen || form.stato === "da_pianificare") return null;
    const valide = sessioni.filter((s) => !sessError(s));
    if (valide.length === 0) return "Aggiungi almeno una sessione di lavoro con tecnico assegnato";
    for (let i = 0; i < sessioni.length; i++) {
      if (!sessioni[i].tecnico_id) return `Sessione ${i + 1}: seleziona un tecnico (non può restare "Non assegnato")`;
    }
    return null;
  }, [form, sessioni, frozen]);

  // ---- Save ----
  const buildPayload = () => {
    const tecnici_ids = [...new Set(sessioni.map((s) => s.tecnico_id).filter(Boolean))];
    return {
      cliente_id: form.cliente_id, luogo: form.luogo, stato: form.stato,
      modalita_addebito: form.modalita_addebito, data_richiesta: form.data_richiesta, data_scadenza: form.data_scadenza,
      zona: form.zona, veicolo: form.veicolo, contratto_id: form.contratto_id === "none" ? null : form.contratto_id,
      sottocontratto_codice: (sottoOptions.some((x) => x.codice === form.sottocontratto_codice)) ? form.sottocontratto_codice : "",
      support_contract_id: form.support_contract_id === "none" ? null : form.support_contract_id,
      richiesta_intervento: form.richiesta_intervento, descrizione_intervento: form.descrizione_intervento,
      note_interne: form.note_interne, tecnici_ids, sessioni_lavoro: sessioni,
      sede_id: form.sede_id || null,
      maps_lat: form.maps_lat ?? null, maps_lng: form.maps_lng ?? null,
      maps_place_id: form.maps_place_id || null, maps_last_geocoded_at: form.maps_last_geocoded_at || null,
    };
  };

  const validate = () => {
    // Intervento completato (congelato): sono modificabili solo Stato e Note interne,
    // il backend ignora gli altri campi. Non applicare la validazione completa.
    if (frozen) return form.stato ? null : "Seleziona lo stato";
    if (!form.cliente_id) return "Seleziona un cliente";
    if (!form.stato) return "Seleziona lo stato";
    if (!form.modalita_addebito) return "Seleziona la modalità di addebito";
    if (!form.contratto_id) return "Seleziona il contratto collegato (oppure \"Nessuno\")";
    if (isEmptyHtml(form.richiesta_intervento)) return "Compila la Richiesta di intervento";
    for (let i = 0; i < sessioni.length; i++) { const e = sessError(sessioni[i]); if (e) return `Sessione ${i + 1}: ${e}`; }
    // Sessione + tecnico obbligatori (in creazione e in modifica), tranne per lo stato "Da pianificare".
    // Il tecnico di ogni sessione non può restare "Non assegnato".
    const statiSenzaSessioni = ["da_pianificare"];
    if (!statiSenzaSessioni.includes(form.stato)) {
      const valide = sessioni.filter((s) => !sessError(s));
      if (valide.length === 0) return "Aggiungi almeno una sessione di lavoro (tecnico, data e orari)";
      for (let i = 0; i < sessioni.length; i++) {
        if (!sessioni[i].tecnico_id) return `Sessione ${i + 1}: seleziona un tecnico (non può restare "Non assegnato")`;
      }
    }
    return null;
  };

  const askOverlap = (items) => new Promise((resolve) => setOverlapDlg({ items, resolve }));
  const checkOverlaps = async () => {
    const found = [];
    for (const s of sessioni) {
      if (!s.tecnico_id || !s.data || !s.ora_inizio || !s.ora_fine) continue;
      try {
        const { data } = await api.post("/attivita/check-overlap", {
          tecnico_id: s.tecnico_id, data: s.data, ora_inizio: s.ora_inizio, ora_fine: s.ora_fine,
          exclude_attivita_id: isNew ? null : id, exclude_session_id: s.session_id || null,
        });
        if (data.overlaps) found.push(...data.items);
      } catch { /* non bloccare su errore tecnico */ }
    }
    // dedup per attivita+session
    const seen = new Set();
    return found.filter((x) => { const k = `${x.attivita_id}|${x.session_id}`; if (seen.has(k)) return false; seen.add(k); return true; });
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return false; }
    if (!frozen && sessioni.length) {
      const ov = await checkOverlaps();
      if (ov.length) { const ok = await askOverlap(ov); if (!ok) return false; }
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/attivita", buildPayload());
        setBaseline(makeSnap(form, sessioni)); // evita warning uscita alla navigazione post-create
        const fromTicket = searchParams.get("from_ticket");
        const fromLavorazione = searchParams.get("from_lavorazione");
        if (fromTicket) {
          try {
            await api.post(`/tickets/${fromTicket}/activities/link`, { attivita_id: data.id, source: "created_from_ticket" });
            toast.success("Intervento creato e collegato al ticket");
          } catch { toast.success("Intervento creato"); }
        } else if (fromLavorazione) {
          try {
            await api.post(`/lavorazioni/${fromLavorazione}/attivita/link`, { attivita_id: data.id });
            toast.success("Intervento creato e collegato alla lavorazione");
          } catch { toast.error("Intervento creato, ma il collegamento alla lavorazione non è riuscito"); }
        } else {
          toast.success("Intervento creato");
        }
        navigate(`/interventi/${data.id}`);
      } else {
        await api.put(`/attivita/${id}`, buildPayload());
        toast.success("Intervento salvato");
        const { data } = await api.get(`/attivita/${id}`);
        const f = buildForm(data);
        setForm(f);
        setSavedStato(f.stato);
        setSessioni(data.sessioni_lavoro || []);
        setAllegati(data.allegati || []);
        setContrattoAlert(data.contratto_alert || null);
        setBaseline(makeSnap(f, data.sessioni_lavoro || []));
      }
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
    finally { setSaving(false); }
  };

  const loadLinkedTickets = () => api.get(`/attivita/${id}/tickets`).then(({ data }) => setLinkedTickets(data)).catch(() => {});
  const searchLinkTickets = async (val) => { setTkLinkQ(val); try { const { data } = await api.get("/tickets/search-for-link", { params: { q: val, cliente_id: form?.cliente_id || "" } }); setTkLinkResults(data); } catch { setTkLinkResults([]); } };
  const linkTicket = async (tid, tCliente) => {
    if (tCliente && form?.cliente_id && tCliente !== form.cliente_id && !window.confirm("Il cliente del ticket è diverso da quello dell'intervento. Collegare comunque?")) return;
    try { await api.post(`/attivita/${id}/tickets/link`, { ticket_id: tid }); toast.success("Ticket collegato"); setTkLinkDlg(false); setTkLinkQ(""); setTkLinkResults([]); loadLinkedTickets(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const unlinkTicket = async (tid) => { if (!window.confirm("Scollegare questo ticket?")) return; try { await api.delete(`/attivita/${id}/tickets/${tid}`); loadLinkedTickets(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const loadLinkedLavs = () => api.get(`/attivita/${id}/lavorazioni`).then(({ data }) => setLinkedLavs(data)).catch(() => {});
  const searchLinkLavs = async (val) => {
    setLavLinkQ(val);
    try { const { data } = await api.get("/lavorazioni", { params: { q: val } }); setLavLinkResults((data || []).slice(0, 30)); }
    catch { setLavLinkResults([]); }
  };
  const linkLav = async (lavId) => {
    if (linkedLavs.some((l) => l.id === lavId)) { toast.info("Lavorazione già collegata"); return; }
    try { await api.post(`/attivita/${id}/lavorazioni/link`, { lavorazione_id: lavId }); toast.success("Lavorazione collegata"); setLavLinkDlg(false); setLavLinkQ(""); setLavLinkResults([]); loadLinkedLavs(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const unlinkLav = async (lavId) => { if (!window.confirm("Scollegare questa lavorazione?")) return; try { await api.delete(`/attivita/${id}/lavorazioni/${lavId}`); loadLinkedLavs(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  // ---- Uscita con modifiche non salvate ----
  const attemptLeave = (target) => { if (dirty) setLeaveTarget(target); else navigate(target); };
  const leaveDiscard = () => { const t = leaveTarget; setLeaveTarget(null); setBaseline(currentSnap); navigate(t); };
  const leaveSave = async () => { const t = leaveTarget; setLeaveTarget(null); const ok = await save(); if (ok) navigate(t); };
  useUnsavedGuard(dirty, () => save());

  // ---- Allegati ----
  const uploadAllegati = async (files) => {
    if (!files || !files.length) return;
    if (isNew) { toast.error("Salva prima l'intervento per aggiungere allegati"); return; }
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    setUploading(true);
    try {
      const { data } = await api.post(`/attivita/${id}/allegati`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAllegati(data.allegati || []);
      toast.success(`${files.length} allegato/i caricato/i`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const downloadAllegato = async (a) => {
    try {
      const res = await api.get(`/attivita/${id}/allegati/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.nome_originale || "allegato";
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeAllegato = async (a) => {
    if (!window.confirm(`Eliminare l'allegato "${a.nome_originale}"?`)) return;
    try {
      const { data } = await api.delete(`/attivita/${id}/allegati/${a.id}`);
      setAllegati(data.allegati || []);
      toast.success("Allegato eliminato");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const duplicate = async () => {
    const def = (cfg?.stati || []).find((s) => s.default && s.attivo !== false) || (cfg?.stati || [])[0];
    const payload = {
      cliente_id: form.cliente_id, luogo: form.luogo, stato: def?.key || "da_pianificare",
      modalita_addebito: form.modalita_addebito, zona: form.zona, veicolo: form.veicolo,
      data_richiesta: new Date().toISOString().slice(0, 10), data_scadenza: "",
      contratto_id: form.contratto_id === "none" ? null : form.contratto_id,
      support_contract_id: form.support_contract_id === "none" ? null : form.support_contract_id,
      richiesta_intervento: form.richiesta_intervento, descrizione_intervento: form.descrizione_intervento,
      note_interne: form.note_interne, tecnici_ids: [], sessioni_lavoro: [],
    };
    try {
      const { data } = await api.post("/attivita", payload);
      toast.success("Attività duplicata");
      navigate(`/interventi/${data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openSendDialog = async () => {
    setSending(true);
    try {
      const ok = await save();
      if (ok === false) { setSending(false); return; }
      const { data } = await api.get(`/attivita/${id}/rapportino/send-preview`);
      setSendForm({
        from: data.from || "", to: data.to || "",
        cc: (data.cc || []).join(", "), bcc: (data.bcc || []).join(", "),
        subject: data.subject || "", body_html: data.body_html || "", firma_html: data.firma_html || "",
        include_pdf: data.include_pdf !== false, filename: data.filename || "rapportino.pdf",
        to_missing: !!data.to_missing, m365_configured: !!data.m365_configured,
      });
      setSendOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const submitSend = async () => {
    setSendLoading(true);
    try {
      const { data } = await api.post(`/attivita/${id}/rapportino/send`, {
        from: sendForm.from, to: sendForm.to, cc: sendForm.cc, bcc: sendForm.bcc,
        subject: sendForm.subject, body_html: sendForm.body_html, firma_html: sendForm.firma_html,
        include_pdf: sendForm.include_pdf,
      });
      toast.success(`Rapportino inviato a ${(data.to || []).join(", ")}`);
      setSendOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendLoading(false); }
  };

  const removeAttivita = async () => {
    if (!window.confirm(`Eliminare definitivamente il rapportino ${numero}? L'operazione non è reversibile. Il numero non verrà riutilizzato.`)) return;
    try { await api.delete(`/attivita/${id}`); toast.success("Intervento eliminato"); navigate("/interventi"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const annullaAttivita = async () => {
    if (!window.confirm(`Segnare l'intervento ${numero} come Annullato? Lo storico verrà mantenuto.`)) return;
    try { const { data } = await api.post(`/attivita/${id}/annulla`); upd("stato", data.stato); toast.success("Intervento annullato"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!form) return <InternalLayout><div className="p-12 text-muted-foreground">Caricamento…</div></InternalLayout>;

  return (
    <InternalLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto w-full">
        {/* Barra comandi sticky */}
        <div className="sticky top-14 z-20 -mx-6 lg:-mx-10 px-6 lg:px-10 py-3 mb-6 bg-background/95 backdrop-blur border-b border-border shadow-sm" data-testid="command-bar">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Button variant="ghost" size="icon" className="rounded-sm shrink-0" onClick={() => attemptLeave(-1)} data-testid="back-btn"><CaretLeft size={20} /></Button>
              <div className="min-w-0">
                <h1 className="font-heading text-xl font-bold flex items-center gap-2 truncate">{isNew ? "Nuovo intervento" : `Intervento ${numero}`}{frozen && <Badge className="rounded-sm gap-1 bg-emerald-100 text-emerald-800 border-emerald-200 shrink-0" data-testid="frozen-badge"><LockSimple size={12} weight="fill" /> Completata · sola lettura</Badge>}</h1>
                {form.cliente_nome && <p className="text-xs text-muted-foreground truncate">{form.cliente_nome}</p>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {dirty && <Badge className="rounded-sm gap-1.5 bg-amber-100 text-amber-800 border-amber-200 animate-pulse" data-testid="unsaved-badge"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Modifiche non salvate</Badge>}
              {!isNew && <Button variant="outline" className="rounded-sm gap-2" data-testid="preview-pdf-btn" onClick={async () => {
                const w = window.open("", "_blank");
                try {
                  const res = await api.get(`/attivita/${id}/pdf`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  if (w) w.location = url; else window.open(url, "_blank");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch { if (w) w.close(); toast.error("Anteprima PDF non disponibile"); }
              }}><FilePdf size={16} /> Anteprima PDF</Button>}
              {!isNew && <Button variant="outline" className="rounded-sm gap-2" onClick={openSendDialog} disabled={sending} data-testid="send-report-btn"><PaperPlaneTilt size={16} /> {sending ? "Attendi…" : "Invia al cliente"}</Button>}
              {!isNew && !frozen && <Button variant="outline" className="rounded-sm gap-2" onClick={duplicate} data-testid="duplicate-btn"><Copy size={16} /> Duplica attività</Button>}
              {!isNew && !frozen && form.stato !== "annullata" && <Button variant="outline" className="rounded-sm gap-2 text-amber-700 border-amber-300 hover:bg-amber-50" onClick={annullaAttivita} data-testid="annulla-btn"><XCircle size={16} /> Annulla intervento</Button>}
              {!isNew && !frozen && <Button variant="outline" className="rounded-sm gap-2 text-destructive border-destructive/40 hover:bg-destructive/5" onClick={removeAttivita} data-testid="delete-btn"><Trash size={16} /> Elimina</Button>}
              <Button className="rounded-sm gap-2" onClick={() => save()} disabled={saving || !!sessionBlock} title={sessionBlock || ""} data-testid="save-detail-btn"><FloppyDisk size={16} /> {saving ? "Salvataggio…" : "Salva"}</Button>
            </div>
          </div>
          {sessionBlock && <p className="text-xs text-amber-700 mt-2 text-right" data-testid="save-block-hint">{sessionBlock}</p>}
        </div>

        {frozen && !statoUnsavedFromCompleted && <div className="mb-5 rounded-sm border border-emerald-200 bg-emerald-50/60 px-4 py-2.5 text-sm text-emerald-900 flex items-center gap-2" data-testid="frozen-banner"><LockSimple size={15} weight="fill" /> Rapportino completato: i campi sono in sola lettura. Puoi modificare solo <b>Stato</b> e <b>Note interne</b>. Cambia lo stato e salva per sbloccarlo.</div>}
        {statoUnsavedFromCompleted && <div className="mb-5 rounded-sm border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 flex items-center gap-2" data-testid="stato-unsaved-banner"><Warning size={16} weight="fill" /> Hai cambiato lo stato dell'intervento. <b>Salva per sbloccare la modifica degli altri campi.</b></div>}

        {lavRef && (
          <div className="mb-5 rounded-sm border border-blue-200 bg-blue-50/60 px-4 py-2.5 text-sm text-blue-900 flex items-center gap-2 flex-wrap" data-testid="lavorazione-origine-banner">
            <LinkSimple size={15} />
            <span>Generato da lavorazione: <b>{lavRef.numero}{lavRef.titolo ? ` — ${lavRef.titolo}` : ""}</b></span>
            <Link to={`/lavorazioni/${lavRef.id}`} className="text-primary hover:underline inline-flex items-center gap-1 ml-1" data-testid="apri-lavorazione-link">Apri lavorazione <ArrowSquareOut size={13} /></Link>
          </div>
        )}

        {/* ===== A. DATI ORGANIZZATIVI ===== */}
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Cliente */}
          <Card className="rounded-sm border-border shadow-none p-5" data-testid="cliente-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider">Cliente</h2>
              {!frozen && <Button variant="outline" size="sm" className="rounded-sm gap-1 h-8" onClick={() => { setNewCli({ ...EMPTY_CLI, ragione_sociale: cliQuery.trim() }); setNewCliOpen(true); }} data-testid="new-anagrafica-btn"><Buildings size={14} /> Crea nuova anagrafica</Button>}
            </div>
            <Label className="text-xs uppercase tracking-wider">Cliente *</Label>
            <div className="relative mt-1" ref={cliBox}>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={cliOpen ? cliQuery : (form.cliente_nome || "")}
                    onChange={(e) => { setCliQuery(e.target.value); setCliOpen(true); }}
                    onFocus={() => { if (!frozen) { setCliOpen(true); setCliQuery(""); } }}
                    disabled={frozen}
                    placeholder="Cerca ragione sociale…" className="rounded-sm pl-8" data-testid="cliente-search" />
                </div>
              </div>
              {cliOpen && cliResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-border rounded-sm shadow-lg max-h-64 overflow-auto" data-testid="cliente-results">
                  {cliResults.map((c) => (
                    <button key={c.id} onClick={() => selectClient(c)} data-testid={`cliente-opt-${c.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-secondary text-sm border-b border-border/50 last:border-0">
                      <div className="font-medium">{c.ragione_sociale}</div>
                      {(c.citta || c.indirizzo) && <div className="text-xs text-muted-foreground">{[c.indirizzo, c.citta].filter(Boolean).join(" · ")}</div>}
                    </button>
                  ))}
                </div>
              )}
              {cliOpen && cliQuery.trim().length >= 2 && cliResults.length === 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-border rounded-sm shadow-lg px-3 py-2 text-sm" data-testid="cliente-noresults">
                  <span className="text-muted-foreground">Nessun cliente trovato.</span>
                  <button onClick={() => { setNewCli({ ...EMPTY_CLI, ragione_sociale: cliQuery.trim() }); setNewCliOpen(true); setCliOpen(false); }} className="text-primary hover:underline ml-1 font-medium" data-testid="new-anagrafica-inline-btn">Crea "{cliQuery.trim()}" →</button>
                </div>
              )}
            </div>
            {form.cliente_id && <Link to={`/clienti/${form.cliente_id}`} className="text-xs text-primary hover:underline mt-1.5 inline-block" data-testid="view-client-link">Visualizza scheda cliente →</Link>}

            {clientSedi.length > 1 && (
              <div className="mt-4" data-testid="sede-block">
                <Label className="text-xs uppercase tracking-wider">Sede intervento</Label>
                <Select value={form.sede_id || ""} onValueChange={pickSede} disabled={frozen}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="detail-sede"><SelectValue placeholder="Seleziona sede" /></SelectTrigger>
                  <SelectContent>{clientSedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome} — {s.indirizzo}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <div className="mt-4">
              <Label className="text-xs uppercase tracking-wider">Luogo / sede intervento</Label>
              <Input value={form.luogo} onChange={(e) => upd("luogo", e.target.value)} disabled={frozen} className="mt-1 rounded-sm" placeholder="Indirizzo di intervento" data-testid="detail-luogo" />

              {/* Mini sezione Maps */}
              <div className="mt-2 rounded-sm border border-border bg-secondary/20 p-2.5" data-testid="maps-mini">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin size={14} className="text-primary" />
                    {mapsStatus === "loading" ? "Ricerca posizione…"
                      : mapsStatus === "found" ? "Posizione trovata"
                      : mapsStatus === "notfound" ? "Posizione non trovata"
                      : mapsStatus === "error" ? "Errore mappa"
                      : "Mappa"}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => geocode(form.luogo)} disabled={frozen || !(form.luogo || "").trim()} data-testid="maps-refresh-btn"><ArrowsClockwise size={13} /> Aggiorna posizione</Button>
                    <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-xs rounded-sm" onClick={openInMaps} disabled={!(form.luogo || "").trim()} data-testid="open-in-maps-btn"><NavigationArrow size={13} weight="fill" /> Apri in Maps</Button>
                  </div>
                </div>
                {mapUrl && <img src={mapUrl} alt="Mappa posizione intervento" className="mt-2 w-full rounded-sm border border-border" data-testid="maps-preview-img" />}
                {mapsConfigured === false && (form.luogo || "").trim() && (
                  <p className="text-[11px] text-muted-foreground mt-2" data-testid="maps-not-configured">API Maps non configurata: anteprima mappa non disponibile. Il pulsante "Apri in Maps" funziona comunque.</p>
                )}
              </div>
            </div>
          </Card>

          {/* Dati intervento */}
          <Card className="rounded-sm border-border shadow-none p-5" data-testid="dati-intervento-card">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3">Dati intervento</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Numero</Label>
                <Input value={numero || "— (assegnato al salvataggio)"} disabled className="mt-1 rounded-sm bg-secondary/50" data-testid="detail-numero" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Stato *</Label>
                <Select value={form.stato} onValueChange={(v) => upd("stato", v)}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="detail-stato"><SelectValue /></SelectTrigger>
                  <SelectContent>{statiOpts.map((s) => <SelectItem key={s.key} value={s.key}><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />{s.label}</span></SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Modalità di addebito *</Label>
                <Select value={form.modalita_addebito} onValueChange={(v) => { modalitaTouchedRef.current = true; upd("modalita_addebito", v); }} disabled={frozen}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="detail-modalita"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{modalitaOpts.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Data richiesta</Label>
                <Input type="date" value={form.data_richiesta} onChange={(e) => upd("data_richiesta", e.target.value)} disabled={frozen} className="mt-1 rounded-sm" data-testid="detail-data-richiesta" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Zona</Label>
                {zoneOpts.length > 0 ? (
                  <Select value={form.zona || "none"} onValueChange={(v) => { zonaTouchedRef.current = true; upd("zona", v === "none" ? "" : v); }} disabled={frozen}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="detail-zona"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{zoneOpts.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Input value={form.zona} onChange={(e) => { zonaTouchedRef.current = true; upd("zona", e.target.value); }} disabled={frozen} className="mt-1 rounded-sm" placeholder="Zona" data-testid="detail-zona-input" />}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Veicolo / mezzo</Label>
                <Select value={form.veicolo || "none"} onValueChange={(v) => upd("veicolo", v === "none" ? "" : v)} disabled={frozen}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="detail-veicolo"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{veicoli.map((v) => <SelectItem key={v.targa} value={v.targa} disabled={v.in_manutenzione}>{v.targa}{v.in_manutenzione ? " · in manutenzione" : ""}</SelectItem>)}{form.veicolo && !veicoli.some((v) => v.targa === form.veicolo) && <SelectItem value={form.veicolo}>{form.veicolo}</SelectItem>}</SelectContent>
                </Select>
              </div>
              {form.modalita_addebito === "contratto_flat" && (
                <div className="col-span-2" data-testid="contratto-flat-legacy-hint">
                  <p className="text-[11px] text-muted-foreground mt-1">Imposta il contratto FLAT nel campo "Contratto collegato" qui sotto.</p>
                </div>
              )}
              <div className="col-span-2" data-testid="contratto-collegato-field">
                <Label className="text-xs uppercase tracking-wider">Contratto collegato *</Label>
                <Select value={form.contratto_id} onValueChange={(v) => { contrattoTouchedRef.current = true; upd("contratto_id", v); }} disabled={frozen || !form.cliente_id}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="detail-contratto">
                    <SelectValue placeholder={form.cliente_id ? "Seleziona (Nessuno o contratto)" : "Seleziona prima il cliente"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno</SelectItem>
                    {linkableContracts.map((c) => {
                      const notSel = c.stato === "sospeso" || c.stato === "cessato";
                      return (
                        <SelectItem key={c.id} value={c.id} disabled={notSel}>
                          {c.nome} · {c.tipo}
                          {c.stato === "sospeso" ? " — sospeso" : ""}
                          {c.stato === "cessato" ? " — chiuso" : ""}
                          {isScaduto(c) ? " — Scaduto" : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {form.cliente_id && linkableContracts.length === 0 && (
                  <p className="text-[11px] text-muted-foreground mt-1">Nessun contratto disponibile (assistenza/flat/manutenzione) per questo cliente.</p>
                )}
                {selectedContract && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="contratto-collegato-info">
                    <span className="text-xs font-mono font-medium">{selectedContract.nome}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-secondary border border-border">{selectedContract.tipo}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary border border-primary/20">{CAT_LABELS[selectedContract.categoria] || selectedContract.categoria}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border capitalize ${selectedContract.stato === "attivo" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{selectedContract.stato}</span>
                    {isScaduto(selectedContract) && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200 font-medium">Scaduto</span>}
                  </div>
                )}
                {sottoOptions.length > 0 && (
                  <div className="mt-2" data-testid="intervento-sottocontratto-field">
                    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Sottocontratto mensile</Label>
                    <Select value={form.sottocontratto_codice || ""} onValueChange={(v) => upd("sottocontratto_codice", v)} disabled={frozen}>
                      <SelectTrigger className="mt-1 rounded-sm" data-testid="detail-sottocontratto"><SelectValue placeholder="Seleziona sottocontratto" /></SelectTrigger>
                      <SelectContent>
                        {sottoOptions.map((so) => <SelectItem key={so.codice} value={so.codice}>{MESI_IT[(Number(so.mese) || 1) - 1]} {so.anno} · {so.codice}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {contrattoAlert && (
                  <div className="mt-2 rounded-sm border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 flex items-center gap-2 text-sm" data-testid="intervento-contratto-alert">
                    <span className="font-medium">⚠</span> {contrattoAlert}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* ===== B. TESTI ===== */}
        <div className="space-y-5 mt-5">
          <Card className="rounded-sm border-border shadow-none p-5" data-testid="richiesta-card">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3">Richiesta di intervento *</h2>
            <RichEditor value={form.richiesta_intervento} onChange={(v) => upd("richiesta_intervento", v)} readOnly={frozen} placeholder="Descrivi la richiesta del cliente / attività da svolgere…" testid="richiesta-editor" />
          </Card>
          {!isNew && (
          <Card className="rounded-sm border-border shadow-none p-5" data-testid="descrizione-card">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3">Descrizione attività svolta</h2>
            <RichEditor value={form.descrizione_intervento} onChange={(v) => upd("descrizione_intervento", v)} readOnly={frozen} placeholder="Descrivi il lavoro eseguito dal tecnico…" testid="descrizione-editor" />
          </Card>
          )}
          <Card className="rounded-sm border-border shadow-none p-5 bg-amber-50/30" data-testid="note-card">
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider mb-1">Note interne</h2>
            <p className="text-[11px] text-muted-foreground mb-3">Visibili solo internamente — non compaiono nel rapportino cliente.</p>
            <RichEditor value={form.note_interne} onChange={(v) => upd("note_interne", v)} placeholder="Note ad uso interno…" testid="note-editor" />
          </Card>
        </div>

        {/* ===== C. SESSIONI ===== */}
        <Card className="rounded-sm border-border shadow-none p-5 mt-5" data-testid="sessioni-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider">Tecnici e sessioni di lavoro</h2>
              <p className="text-[11px] text-muted-foreground">Chi ha fatto cosa. Le ore sono calcolate al netto della pausa. Obbligatorie con tecnico assegnato, tranne per lo stato "Da pianificare".</p>
            </div>
            {!frozen && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={openMultiAdd} className="rounded-sm gap-1" data-testid="add-multi-tecnico-btn"><UserPlus size={14} /> Più tecnici</Button>
                <Button variant="outline" size="sm" onClick={addSess} className="rounded-sm gap-1" data-testid="add-sessione-btn"><Plus size={14} /> Aggiungi sessione</Button>
              </div>
            )}
          </div>

          {sessioni.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Nessuna sessione. Aggiungi una sessione oppure salva come "Da pianificare".</p>
          ) : (
            <div className="space-y-3">
              {sessioni.map((s, i) => {
                const err = sessError(s);
                return (
                  <div key={i} className={`rounded-sm border p-3 ${err ? "border-destructive" : "border-border"}`} data-testid={`sessione-row-${i}`}>
                    <div className="grid grid-cols-2 md:grid-cols-[repeat(21,minmax(0,1fr))] gap-2 items-end">
                      <div className="col-span-2 md:col-span-4">
                        <Label className="text-[10px] uppercase tracking-wider">Tecnico</Label>
                        <Select value={s.tecnico_id || "none"} onValueChange={(v) => updSess(i, "tecnico_id", v === "none" ? "" : v)} disabled={frozen}>
                          <SelectTrigger className="rounded-sm h-9" data-testid={`sess-tecnico-${i}`}><SelectValue placeholder="Tecnico" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">— Non assegnato —</SelectItem>{tecnici.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-3"><Label className="text-[10px] uppercase tracking-wider whitespace-nowrap">Data</Label>
                        <div className="relative">
                          <Input type="date" value={(s.data || "").slice(0, 10)} onChange={(e) => updSess(i, "data", e.target.value)} disabled={frozen} className="rounded-sm h-9 pr-8 [&::-webkit-calendar-picker-indicator]:opacity-0" data-testid={`sess-data-${i}`} />
                          <button type="button" disabled={frozen} tabIndex={-1} title="Apri calendario" onClick={(e) => { const inp = e.currentTarget.parentElement.querySelector("input"); if (!inp) return; try { inp.showPicker(); } catch { inp.focus(); } }} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-40" data-testid={`sess-data-cal-${i}`}><CalendarBlank size={16} /></button>
                        </div>
                      </div>
                      <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-wider whitespace-nowrap">Inizio</Label><TimeField value={s.ora_inizio} onChange={(v) => updSess(i, "ora_inizio", v)} step={slotStep} disabled={frozen} testid={`sess-inizio-${i}`} /></div>
                      <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-wider whitespace-nowrap">Fine</Label><TimeField value={s.ora_fine} onChange={(v) => updSess(i, "ora_fine", v)} step={slotStep} disabled={frozen} testid={`sess-fine-${i}`} /></div>
                      <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-wider whitespace-nowrap">Pausa da</Label><TimeField value={s.pausa_inizio} onChange={(v) => updSess(i, "pausa_inizio", v)} step={slotStep} disabled={frozen} testid={`sess-pausa-inizio-${i}`} /></div>
                      <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-wider whitespace-nowrap">Pausa a</Label><TimeField value={s.pausa_fine} onChange={(v) => updSess(i, "pausa_fine", v)} step={slotStep} disabled={frozen} testid={`sess-pausa-fine-${i}`} /></div>
                      <div className="md:col-span-1"><Label className="text-[10px] uppercase tracking-wider whitespace-nowrap">Ore</Label><div className="h-9 flex items-center text-sm font-medium" data-testid={`sess-durata-${i}`}>{hm(sessMin(s))}</div></div>
                      <div className="col-span-2 md:col-span-3"><Label className="text-[10px] uppercase tracking-wider whitespace-nowrap">Modalità addebito</Label>
                        <Select value={s.modalita_addebito || "none"} onValueChange={(v) => updSess(i, "modalita_addebito", v === "none" ? "" : v)} disabled={frozen}>
                          <SelectTrigger className="rounded-sm h-9" data-testid={`sess-modalita-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">— come intervento —</SelectItem>{modalitaOpts.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {!frozen && (
                        <div className="col-span-2 md:col-span-2 flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" title="Clona su altri tecnici" onClick={() => openClone(i)} data-testid={`sess-clone-${i}`}><UserPlus size={16} /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeSess(i)} data-testid={`sess-remove-${i}`}><Trash size={15} /></Button>
                        </div>
                      )}
                    </div>
                    {err && <p className="text-xs text-destructive mt-2" data-testid={`sess-error-${i}`}>{err}</p>}
                  </div>
                );
              })}

              {/* Riepilogo */}
              <div className="border-t border-border pt-3 mt-1 space-y-1" data-testid="riepilogo-tempi">
                {Object.entries(perTec).map(([tid, mn]) => (
                  <div key={tid} className="flex items-center justify-between text-sm">
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tecColor(tid) }} />{tecName(tid)}</span>
                    <span className="font-medium">{hm(mn)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-sm font-bold pt-1 border-t border-border">
                  <span>Totale intervento</span><span data-testid="riepilogo-totale">{hm(totMin)}</span>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* ===== D. ALLEGATI ===== */}
        <DropZone onFiles={(fl) => uploadAllegati(Array.from(fl))} className="block">
        <Card className="rounded-sm border-border shadow-none p-5 mt-5" data-testid="allegati-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider flex items-center gap-2"><Paperclip size={15} /> Allegati</h2>
              <p className="text-[11px] text-muted-foreground">File collegati all'intervento (max 100 MB ciascuno). Non inclusi nel rapportino cliente.</p>
            </div>
            {!frozen && !isNew && (
              <>
                <input ref={fileRef} type="file" multiple className="hidden" data-testid="allegati-input" onChange={(e) => uploadAllegati(Array.from(e.target.files || []))} />
                <Button variant="outline" size="sm" className="rounded-sm gap-1" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="allegati-upload-btn"><UploadSimple size={14} /> {uploading ? "Caricamento…" : "Carica file"}</Button>
              </>
            )}
          </div>
          {isNew ? (
            <p className="text-sm text-muted-foreground py-2">Salva l'intervento per poter aggiungere allegati.</p>
          ) : allegati.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2" data-testid="allegati-empty">Nessun allegato.</p>
          ) : (
            <div className="divide-y divide-border border border-border rounded-sm" data-testid="allegati-list">
              {allegati.map((a) => (
                <div key={a.id} className="flex items-center gap-3 px-3 py-2" data-testid={`allegato-row-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{a.nome_originale}</span>
                      {a.file_potenzialmente_eseguibile && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200 shrink-0" title="File potenzialmente eseguibile: solo download, mai eseguito" data-testid={`allegato-exec-${a.id}`}><Warning size={11} weight="fill" /> potenzialmente eseguibile</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{fmtSize(a.size)} · {a.uploaded_by || "—"} · {new Date(a.uploaded_at).toLocaleString("it-IT")}</div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-primary shrink-0" onClick={() => downloadAllegato(a)} data-testid={`allegato-download-${a.id}`}><DownloadSimple size={16} /></Button>
                  {!frozen && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => removeAllegato(a)} data-testid={`allegato-delete-${a.id}`}><Trash size={15} /></Button>}
                </div>
              ))}
            </div>
          )}
        </Card>
        </DropZone>

        {!isNew && (
          <Card className="rounded-sm border-border shadow-none p-5 mt-5" data-testid="ticket-collegati-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider flex items-center gap-2"><Ticket size={15} /> Ticket collegati</h2>
              <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={() => { setTkLinkDlg(true); searchLinkTickets(""); }} data-testid="link-ticket-btn"><LinkSimple size={14} /> Collega ticket</Button>
            </div>
            {frozen && linkedTickets.some((t) => !t.is_final) && (
              <div className="mb-3 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-center gap-2" data-testid="open-tickets-alert">
                <Warning size={16} /> Ci sono ticket collegati ancora aperti.
              </div>
            )}
            {linkedTickets.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessun ticket collegato.</p>
            ) : (
              <div className="divide-y divide-border border border-border rounded-sm">
                {linkedTickets.map((tk) => (
                  <div key={tk.id} className="flex items-center gap-3 p-2.5" data-testid={`linked-ticket-${tk.id}`}>
                    <Ticket size={16} className="text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{tk.numero_ticket} · {tk.oggetto}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        {tk.stato && <span className="px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: (tk.stato.colore || "#e2e8f0") + "22", color: tk.stato.colore || "#475569" }}>{tk.stato.nome}</span>}
                        {tk.priorita && <span>{tk.priorita.nome}</span>}
                        {tk.richiedente_nome && <span>· {tk.richiedente_nome}</span>}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => window.open(`/helpdesk/ticket/${tk.id}`, "_blank")} title="Apri ticket" data-testid={`open-ticket-${tk.id}`}><ArrowSquareOut size={15} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => unlinkTicket(tk.id)} title="Scollega" data-testid={`unlink-ticket-${tk.id}`}><Trash size={15} /></Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        <Dialog open={tkLinkDlg} onOpenChange={setTkLinkDlg}>
          <DialogContent className="rounded-sm max-w-xl" data-testid="link-ticket-dialog">
            <DialogHeader><DialogTitle>Collega ticket esistente</DialogTitle></DialogHeader>
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={tkLinkQ} onChange={(e) => searchLinkTickets(e.target.value)} placeholder="Cerca ticket per numero, oggetto, richiedente…" className="pl-8 rounded-sm" data-testid="link-ticket-search" autoFocus />
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-border border border-border rounded-sm">
              {tkLinkResults.length === 0 ? <p className="text-sm text-muted-foreground p-4">Nessun ticket trovato.</p> : tkLinkResults.map((tk) => (
                <button key={tk.id} onClick={() => linkTicket(tk.id, tk.cliente_id)} className="w-full text-left p-3 hover:bg-secondary flex items-center gap-3" data-testid={`tk-link-result-${tk.id}`}>
                  <Ticket size={16} className="text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{tk.numero_ticket} · {tk.oggetto} {tk.cliente_id && form?.cliente_id && tk.cliente_id !== form.cliente_id && <span className="text-[10px] text-amber-600">(cliente diverso)</span>}</div>
                    <div className="text-xs text-muted-foreground">{(tk.stato || {}).nome} · {tk.richiedente_nome || "—"}</div>
                  </div>
                  <LinkSimple size={15} className="text-blue-600 shrink-0" />
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {!isNew && (
          <Card className="rounded-sm border-border shadow-none p-5 mt-5" data-testid="lavorazioni-collegate-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider flex items-center gap-2"><Wrench size={15} /> Lavorazioni collegate {linkedLavs.length > 0 && <span className="text-xs text-muted-foreground">({linkedLavs.length})</span>}</h2>
              <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={() => { setLavLinkDlg(true); searchLinkLavs(""); }} data-testid="link-lavorazione-btn"><LinkSimple size={14} /> Collega lavorazione</Button>
            </div>
            {linkedLavs.length > 0 && (linkedLavs.length > 1 || (linkedLavs[0]?.interventi_count || 1) > 1) && (
              <div className="mb-3 rounded-sm border border-slate-300 bg-slate-50 px-3 py-2 flex items-start gap-2.5" data-testid="att-sync-off-banner">
                <LinkBreak size={18} className="text-slate-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0 text-xs text-slate-700">
                  <span className="font-semibold">Sincronizzazione automatica disattivata</span> — collegamenti multipli (più lavorazioni o lavorazione con più interventi). Gli stati di intervento e lavorazione vanno aggiornati manualmente.
                </div>
              </div>
            )}
            {linkedLavs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessuna lavorazione collegata.</p>
            ) : (
              <div className="divide-y divide-border border border-border rounded-sm">
                {linkedLavs.map((lv) => (
                  <div key={lv.id} className="flex items-center gap-3 p-2.5" data-testid={`linked-lavorazione-${lv.id}`}>
                    <Wrench size={16} className="text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{lv.numero_lavorazione}{lv.titolo_lavorazione ? ` · ${lv.titolo_lavorazione}` : ""}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                        {lv.fase_label && <span className="px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: (lv.fase_color || "#e2e8f0") + "22", color: lv.fase_color || "#475569" }}>{lv.fase_label}</span>}
                        {lv.cliente_nome && <span>· {lv.cliente_nome}</span>}
                        {lv.interventi_count > 1 && <span className="text-amber-600">· {lv.interventi_count} interventi (sync off)</span>}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => window.open(`/lavorazioni/${lv.id}`, "_blank")} title="Apri lavorazione" data-testid={`open-lavorazione-${lv.id}`}><ArrowSquareOut size={15} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => unlinkLav(lv.id)} title="Scollega" data-testid={`unlink-lavorazione-${lv.id}`}><LinkBreak size={15} /></Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        <Dialog open={lavLinkDlg} onOpenChange={setLavLinkDlg}>
          <DialogContent className="rounded-sm max-w-xl" data-testid="link-lavorazione-dialog">
            <DialogHeader><DialogTitle>Collega lavorazione esistente</DialogTitle><DialogDescription>Cerca e seleziona una lavorazione da collegare a questo intervento.</DialogDescription></DialogHeader>
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={lavLinkQ} onChange={(e) => searchLinkLavs(e.target.value)} placeholder="Cerca lavorazione per numero, titolo, cliente…" className="pl-8 rounded-sm" data-testid="link-lavorazione-search" autoFocus />
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-border border border-border rounded-sm">
              {lavLinkResults.length === 0 ? <p className="text-sm text-muted-foreground p-4">Nessuna lavorazione trovata.</p> : lavLinkResults.map((lv) => {
                const already = linkedLavs.some((x) => x.id === lv.id);
                return (
                  <button key={lv.id} onClick={() => !already && linkLav(lv.id)} disabled={already} className="w-full text-left p-3 hover:bg-secondary flex items-center gap-3 disabled:opacity-50" data-testid={`lav-link-result-${lv.id}`}>
                    <Wrench size={16} className="text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{lv.numero_lavorazione}{lv.titolo_lavorazione ? ` · ${lv.titolo_lavorazione}` : ""} {already && <span className="text-[10px] text-emerald-600">(già collegata)</span>}</div>
                      <div className="text-xs text-muted-foreground">{lv.cliente_nome || "—"}</div>
                    </div>
                    {!already && <LinkSimple size={15} className="text-blue-600 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>

      </div>

      <AlertDialog open={!!leaveTarget} onOpenChange={(o) => { if (!o) setLeaveTarget(null); }}>
        <AlertDialogContent className="rounded-sm" data-testid="leave-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><Warning size={20} className="text-amber-500" /> Modifiche non salvate</AlertDialogTitle>
            <AlertDialogDescription>Ci sono modifiche non salvate in questa scheda. Vuoi uscire senza salvare?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel className="rounded-sm" data-testid="leave-stay-btn" onClick={() => setLeaveTarget(null)}>Resta nella pagina</AlertDialogCancel>
            <Button variant="outline" className="rounded-sm text-destructive border-destructive/40 hover:bg-destructive/5" data-testid="leave-discard-btn" onClick={leaveDiscard}>Esci senza salvare</Button>
            <AlertDialogAction className="rounded-sm" data-testid="leave-save-btn" onClick={(e) => { e.preventDefault(); leaveSave(); }} disabled={saving}>Salva ed esci</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!overlapDlg} onOpenChange={(o) => { if (!o && overlapDlg) { overlapDlg.resolve(false); setOverlapDlg(null); } }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><Warning size={20} className="text-amber-500" /> Sovrapposizione rilevata</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground space-y-1.5">
                <p>Una o più sessioni si sovrappongono ad altri interventi per lo stesso tecnico:</p>
                <ul className="space-y-1">
                  {(overlapDlg?.items || []).map((it, i) => (
                    <li key={i} className="rounded-sm border border-amber-200 bg-amber-50/50 px-2 py-1 text-foreground" data-testid={`overlap-item-${i}`}>
                      <b>n. {it.numero || "—"}</b> · {it.cliente_nome || "Cliente"} · {tecName(it.tecnico_id) || "Tecnico"} · {it.ora_inizio}–{it.ora_fine}
                    </li>
                  ))}
                </ul>
                <p>Vuoi salvare comunque?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="overlap-cancel-btn" onClick={() => { overlapDlg?.resolve(false); setOverlapDlg(null); }}>Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm" data-testid="overlap-confirm-btn" onClick={() => { overlapDlg?.resolve(true); setOverlapDlg(null); }}>Salva comunque</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clone dialog */}
      <Dialog open={cloneIdx != null} onOpenChange={(o) => { if (!o) setCloneIdx(null); }}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">Clona sessione su altri tecnici</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Copia orari, pausa, km, sconti, modalità e note su una nuova sessione per ogni tecnico selezionato.</p>
          <div className="space-y-1.5 max-h-64 overflow-auto py-2">
            {tecnici.map((t) => {
              const checked = cloneTecs.includes(t.id);
              const isSrc = cloneIdx != null && sessioni[cloneIdx]?.tecnico_id === t.id;
              return (
                <label key={t.id} className={`flex items-center gap-2.5 px-2 py-1.5 rounded-sm ${isSrc ? "opacity-50 cursor-not-allowed" : "hover:bg-secondary cursor-pointer"}`} data-testid={`clone-tec-${t.id}`}>
                  <input type="checkbox" checked={checked} disabled={isSrc} onChange={(e) => setCloneTecs((c) => e.target.checked ? [...c, t.id] : c.filter((x) => x !== t.id))} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tecColor(t.id) }} />
                  <span className="text-sm">{t.name}{isSrc && <span className="text-xs text-muted-foreground"> (già selezionato — non clonabile)</span>}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setCloneIdx(null)}>Annulla</Button>
            <Button className="rounded-sm" onClick={doClone} disabled={cloneTecs.length === 0} data-testid="clone-confirm-btn">Clona ({cloneTecs.length})</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aggiungi sessioni per più tecnici (checkbox) */}
      <Dialog open={multiAddOpen} onOpenChange={setMultiAddOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">Aggiungi sessione per più tecnici</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Crea una sessione di lavoro per ciascun tecnico selezionato. Potrai regolare orari e pause per ognuno.</p>
          <div className="space-y-1.5 max-h-64 overflow-auto py-2">
            {tecnici.map((t) => {
              const checked = multiAddTecs.includes(t.id);
              return (
                <label key={t.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-sm hover:bg-secondary cursor-pointer" data-testid={`multi-tec-${t.id}`}>
                  <input type="checkbox" checked={checked} onChange={(e) => setMultiAddTecs((c) => e.target.checked ? [...c, t.id] : c.filter((x) => x !== t.id))} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tecColor(t.id) }} />
                  <span className="text-sm">{t.name}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setMultiAddOpen(false)}>Annulla</Button>
            <Button className="rounded-sm" onClick={doMultiAdd} disabled={multiAddTecs.length === 0} data-testid="multi-add-confirm-btn">Aggiungi ({multiAddTecs.length})</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick-create anagrafica */}
      <Dialog open={newCliOpen} onOpenChange={setNewCliOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Nuova anagrafica cliente</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">Crea rapidamente il cliente: verrà selezionato subito nell'intervento senza perdere i dati già compilati. Puoi completarne i dettagli in seguito dalla scheda cliente.</p>
          <div className="grid grid-cols-2 gap-3 py-1">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Ragione sociale *</Label>
              <Input value={newCli.ragione_sociale} onChange={(e) => setNewCli((f) => ({ ...f, ragione_sociale: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-cli-ragione-sociale" autoFocus />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Indirizzo</Label>
              <Input value={newCli.indirizzo} onChange={(e) => setNewCli((f) => ({ ...f, indirizzo: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-cli-indirizzo" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Città</Label>
              <Input value={newCli.citta} onChange={(e) => setNewCli((f) => ({ ...f, citta: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-cli-citta" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">CAP</Label>
              <Input value={newCli.cap} onChange={(e) => setNewCli((f) => ({ ...f, cap: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-cli-cap" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input value={newCli.email} onChange={(e) => setNewCli((f) => ({ ...f, email: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-cli-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Telefono</Label>
              <Input value={newCli.telefono} onChange={(e) => setNewCli((f) => ({ ...f, telefono: e.target.value }))} className="mt-1 rounded-sm" data-testid="new-cli-telefono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setNewCliOpen(false)}>Annulla</Button>
            <Button className="rounded-sm gap-2" onClick={createAnagrafica} disabled={newCliSaving} data-testid="new-cli-save-btn"><Buildings size={16} /> {newCliSaving ? "Creazione…" : "Crea e seleziona"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invio rapportino al cliente */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><PaperPlaneTilt size={18} /> Invia rapportino al cliente</DialogTitle></DialogHeader>
          {sendForm && (
            <div className="space-y-3">
              {!sendForm.m365_configured && <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-3 py-2" data-testid="send-m365-warning">Nessuna casella Helpdesk predefinita configurata: l'invio non sarà possibile finché non ne imposti una come predefinita in Helpdesk → Caselle email.</div>}
              {sendForm.to_missing && <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-3 py-2" data-testid="send-noemail-warning">Il cliente non ha un'email in anagrafica: inserisci manualmente il destinatario.</div>}
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Da (casella Helpdesk predefinita)</Label>
                <Input value={sendForm.from} readOnly disabled placeholder="Casella Helpdesk predefinita" className="mt-1 rounded-sm h-9 bg-muted/50" data-testid="send-from" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">A (destinatari)</Label>
                <Input value={sendForm.to} onChange={(e) => setSendForm((s) => ({ ...s, to: e.target.value }))} placeholder="email separate da , o ;" className="mt-1 rounded-sm h-9" data-testid="send-to" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">CC</Label>
                  <Input value={sendForm.cc} onChange={(e) => setSendForm((s) => ({ ...s, cc: e.target.value }))} className="mt-1 rounded-sm h-9" data-testid="send-cc" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">CCN</Label>
                  <Input value={sendForm.bcc} onChange={(e) => setSendForm((s) => ({ ...s, bcc: e.target.value }))} className="mt-1 rounded-sm h-9" data-testid="send-bcc" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Oggetto</Label>
                <Input value={sendForm.subject} onChange={(e) => setSendForm((s) => ({ ...s, subject: e.target.value }))} className="mt-1 rounded-sm h-9" data-testid="send-subject" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Testo email</Label>
                <div className="mt-1"><RichEditor value={sendForm.body_html} onChange={(v) => setSendForm((s) => ({ ...s, body_html: v }))} testid="send-body" placeholder="Testo email…" /></div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider">Firma</Label>
                <div className="mt-1"><RichEditor value={sendForm.firma_html} onChange={(v) => setSendForm((s) => ({ ...s, firma_html: v }))} testid="send-firma" placeholder="Firma…" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="send-allegato">
                <FilePdf size={18} className="text-primary shrink-0" />
                <input type="checkbox" checked={sendForm.include_pdf} onChange={(e) => setSendForm((s) => ({ ...s, include_pdf: e.target.checked }))} data-testid="send-include-pdf" />
                <span>Allega PDF rapportino:</span>
                <span className="font-medium truncate" data-testid="send-pdf-filename">{sendForm.filename}</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSendOpen(false)} data-testid="send-cancel-btn">Annulla</Button>
            <Button className="rounded-sm gap-2" onClick={submitSend} disabled={sendLoading} data-testid="send-submit-btn"><PaperPlaneTilt size={16} /> {sendLoading ? "Invio…" : "Invia email"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </InternalLayout>
  );
}
